import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import VerifiedBadge from '../components/VerifiedBadge';
import api from '../api';
import { uploadImage } from '../api';

/**
 * Phase 9.7 — Messages (1:1 DMs).
 *
 * Two behaviours in one page:
 *   /messages          → inbox view (thread list on desktop; empty right pane)
 *   /messages/:handle  → thread with that specific peer opened
 *
 * On mobile the layout collapses: threads list when no peer selected,
 * full-screen thread when one is open. A "‹ Back" button returns to the list.
 */

function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ThreadList({ threads, activeHandle, onOpen }) {
  return (
    <div className="dm-threads" data-testid="dm-thread-list">
      {threads.length === 0 && (
        <div className="dm-empty">
          <div className="dm-empty-emoji">💬</div>
          <div className="dm-empty-title">No conversations yet</div>
          <div className="dm-empty-sub">Tap the Message button on any creator's profile to start one.</div>
        </div>
      )}
      {threads.map((t) => (
        <button
          key={t.thread_id}
          className={`dm-thread-row${activeHandle === t.peer.handle ? ' active' : ''}${t.unread_count > 0 ? ' unread' : ''}`}
          onClick={() => onOpen(t.peer.handle)}
          data-testid={`dm-thread-${t.peer.handle}`}
        >
          <div className="dm-thread-avatar" style={{ background: `linear-gradient(135deg, ${t.peer.gradient[0]}, ${t.peer.gradient[1]})` }}>
            {t.peer.initials}
          </div>
          <div className="dm-thread-body">
            <div className="dm-thread-name">
              {t.peer.display_name}
              {t.peer.verified && <VerifiedBadge size="sm" />}
              <span className="dm-thread-time">{fmtWhen(t.last_message_at)}</span>
            </div>
            <div className="dm-thread-preview">
              {t.last_sender_is_me && <span className="dm-you-tag">You: </span>}
              {t.last_message_preview || '(new conversation)'}
            </div>
          </div>
          {t.unread_count > 0 && <span className="dm-unread-dot">{t.unread_count}</span>}
        </button>
      ))}
    </div>
  );
}

function ThreadView({ handle, onBack, onSent }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);   // {url, uploading}
  const scroller = useRef(null);
  const fileRef = useRef(null);
  const textRef = useRef(null);

  const load = () => {
    api.get(`/dm/thread/${handle}`)
      .then(({ data }) => setData(data))
      .catch((ex) => setErr(ex.response?.data?.detail || 'Could not load conversation.'));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [handle]);
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [data?.messages?.length]);

  // Block toggle for this thread. Uses the peer.is_blocked_by_me flag
  // hydrated by the backend on /dm/thread/{handle}.
  const [blockBusy, setBlockBusy] = useState(false);
  const toggleBlock = async () => {
    if (!handle || blockBusy) return;
    const wasBlocked = !!data?.peer?.is_blocked_by_me;
    setBlockBusy(true);
    try {
      if (wasBlocked) {
        await api.delete(`/dm/block/${handle}`);
      } else {
        await api.post(`/dm/block/${handle}`);
      }
      setData((prev) => (prev
        ? { ...prev, peer: { ...prev.peer, is_blocked_by_me: !wasBlocked } }
        : prev));
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Could not update block status.');
    } finally {
      setBlockBusy(false);
    }
  };

  // Poll every 8s while open — WebSocket is a post-launch upgrade
  useEffect(() => {
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [handle]);

  const insertEmoji = (e) => {
    const ta = textRef.current;
    if (!ta) { setText((t) => t + e); return; }
    const start = ta.selectionStart ?? text.length;
    const end = ta.selectionEnd ?? text.length;
    const next = text.slice(0, start) + e + text.slice(end);
    setText(next);
    // restore focus and cursor after the inserted char
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + e.length;
    });
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';   // allow re-selecting same file
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setErr('Only image attachments are supported.');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setErr('Image must be under 5MB.');
      return;
    }
    setPendingImage({ url: null, uploading: true });
    setErr('');
    try {
      const { url } = await uploadImage(f);
      setPendingImage({ url, uploading: false });
    } catch (ex) {
      setPendingImage(null);
      setErr(ex.response?.data?.detail || ex.message || 'Upload failed.');
    }
  };

  const send = async () => {
    const body = text.trim();
    const image_url = pendingImage?.url || null;
    if (sending) return;
    if (!body && !image_url) return;
    if (pendingImage?.uploading) return;
    setSending(true);
    setErr('');
    try {
      const { data: r } = await api.post('/dm/send', {
        to_handle: handle,
        body,
        ...(image_url ? { image_url } : {}),
      });
      setData((d) => d ? { ...d, messages: [...d.messages, r.message] } : d);
      setText('');
      setPendingImage(null);
      setEmojiOpen(false);
      onSent && onSent();
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Message failed to send.');
    } finally {
      setSending(false);
    }
  };

  if (err && !data) {
    return <div className="dm-thread-err" data-testid="dm-thread-err">{err}</div>;
  }
  if (!data) {
    return <div className="dm-loading">Loading…</div>;
  }

  return (
    <div className="dm-thread-view" data-testid="dm-thread-view">
      <div className="dm-thread-head">
        {onBack && <button className="dm-back-btn" onClick={onBack} data-testid="dm-back">‹</button>}
        <div className="dm-thread-head-avatar" style={{ background: `linear-gradient(135deg, ${data.peer.gradient[0]}, ${data.peer.gradient[1]})` }}>
          {data.peer.initials}
        </div>
        <div className="dm-thread-head-info">
          <div className="dm-thread-head-name">
            {data.peer.display_name} {data.peer.verified && <VerifiedBadge size="sm" />}
          </div>
          <div className="dm-thread-head-handle">
            @{data.peer.handle}
            {data.peer.dm_gating === 'bit_gated' && (
              <span className="dm-gating-badge" title="Bit holders only">💎 Bit-gated</span>
            )}
          </div>
        </div>
        <button
          type="button"
          className={`mc-btn-block${data.peer.is_blocked_by_me ? ' active' : ''} dm-thread-block-btn`}
          onClick={toggleBlock}
          disabled={blockBusy}
          data-testid="dm-block-btn"
          title={data.peer.is_blocked_by_me
            ? `Unblock @${data.peer.handle}`
            : `Block @${data.peer.handle} from DM-ing you`}
        >
          🚫 {data.peer.is_blocked_by_me ? 'Unblock' : 'Block'}
        </button>
      </div>

      <div className="dm-thread-scroller" ref={scroller}>
        {data.messages.length === 0 && (
          <div className="dm-conv-empty">Say hi to @{data.peer.handle} — messages start here.</div>
        )}
        {(() => {
          const peerReadTs = data.peer_read_at ? new Date(data.peer_read_at).getTime() : 0;
          // Find the last of MY messages that the peer has already opened.
          // That's the only place we surface the "Seen" tick — matches X/iMessage.
          let lastSeenMineIdx = -1;
          data.messages.forEach((m, i) => {
            if (m.is_mine && peerReadTs && new Date(m.created_at).getTime() <= peerReadTs) {
              lastSeenMineIdx = i;
            }
          });
          return data.messages.map((m, i) => (
            <div
              key={m.id}
              className={`dm-msg${m.is_mine ? ' mine' : ''}`}
              data-testid={m.is_mine ? 'dm-msg-mine' : 'dm-msg-theirs'}
            >
              <div className="dm-msg-bubble">
                {m.image_url && (
                  <a href={m.image_url} target="_blank" rel="noopener noreferrer" className="dm-msg-image-wrap">
                    <img src={m.image_url} alt="" className="dm-msg-image" data-testid="dm-msg-image" />
                  </a>
                )}
                {m.body && <div className="dm-msg-body">{m.body}</div>}
              </div>
              <div className="dm-msg-time">
                {fmtWhen(m.created_at)}
                {m.is_mine && i === lastSeenMineIdx && (
                  <span className="dm-msg-seen" data-testid="dm-msg-seen" title="Seen">
                    <svg viewBox="0 0 16 12" width="14" height="10" fill="none" aria-hidden="true">
                      <path d="M1 6.5l3.2 3.2L10 3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M6 6.5l3.2 3.2L15 3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Seen
                  </span>
                )}
              </div>
            </div>
          ));
        })()}
      </div>

      <div className="dm-composer" data-testid="dm-composer">
        {err && <div className="dm-err" data-testid="dm-send-err">{err}</div>}

        {pendingImage && (
          <div className="dm-attachment-preview" data-testid="dm-attachment-preview">
            {pendingImage.uploading ? (
              <div className="dm-attachment-uploading">Uploading…</div>
            ) : (
              <img src={pendingImage.url} alt="" />
            )}
            <button
              className="dm-attachment-remove"
              onClick={() => setPendingImage(null)}
              aria-label="Remove attachment"
              data-testid="dm-attachment-remove"
            >×</button>
          </div>
        )}

        {emojiOpen && (
          <EmojiPicker onPick={(e) => { insertEmoji(e); }} onClose={() => setEmojiOpen(false)} />
        )}

        <div className="dm-composer-bar">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onPickFile}
            style={{ display: 'none' }}
            data-testid="dm-file-input"
          />
          <button
            className="dm-icon-btn"
            onClick={() => fileRef.current?.click()}
            aria-label="Attach image"
            data-testid="dm-attach-btn"
            title="Attach image"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
              <path d="M13 6L6.7 12.3a4 4 0 105.66 5.66L20 10.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M12 15l-2.83 2.83a2 2 0 11-2.83-2.83L14 6.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
          <button
            className={`dm-icon-btn${emojiOpen ? ' active' : ''}`}
            onClick={() => setEmojiOpen((v) => !v)}
            aria-label="Emoji"
            data-testid="dm-emoji-btn"
            title="Emoji"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <circle cx="9" cy="10" r="1" fill="currentColor" />
              <circle cx="15" cy="10" r="1" fill="currentColor" />
              <path d="M8 14c1 1.5 2.5 2.5 4 2.5s3-1 4-2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
          <textarea
            ref={textRef}
            className="dm-input"
            rows={1}
            placeholder={`Message @${data.peer.handle}…`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            data-testid="dm-input"
          />
          <button
            className="dm-send-btn"
            onClick={send}
            disabled={sending || pendingImage?.uploading || (!text.trim() && !pendingImage?.url)}
            data-testid="dm-send-btn"
            aria-label="Send"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
              <path d="M4 12l17-8-8 17-2.5-6.5L4 12z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}


// Curated emoji picker — 64 popular symbols in a 8-col grid. Zero deps.
const EMOJI_SET = [
  '😀', '😂', '🤣', '😊', '😍', '🥰', '😘', '😎',
  '🤩', '🥳', '😇', '🙃', '😉', '😜', '🤪', '🤗',
  '🤔', '🤨', '😐', '😑', '🙄', '😴', '🥱', '🤤',
  '😢', '😭', '😤', '😡', '🤬', '🤯', '😳', '🥵',
  '👍', '👎', '👏', '🙌', '🤝', '🙏', '💪', '🫶',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '💯',
  '🔥', '✨', '⭐', '🎉', '🎊', '🎁', '🏆', '💎',
  '☕', '🍕', '🍔', '🌮', '🍩', '🍪', '🍿', '🎵',
];

function EmojiPicker({ onPick, onClose }) {
  return (
    <div className="dm-emoji-picker" data-testid="dm-emoji-picker">
      <div className="dm-emoji-picker-head">
        <span>Pick an emoji</span>
        <button onClick={onClose} aria-label="Close emoji picker">×</button>
      </div>
      <div className="dm-emoji-grid">
        {EMOJI_SET.map((e) => (
          <button
            key={e}
            className="dm-emoji-btn"
            onClick={() => onPick(e)}
            data-testid={`dm-emoji-${e}`}
            type="button"
          >{e}</button>
        ))}
      </div>
    </div>
  );
}

export default function Messages() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);

  const loadThreads = () => {
    api.get('/dm/threads').then(({ data }) => setThreads(data.threads || [])).catch(() => {});
  };

  useEffect(() => { loadThreads(); }, []);

  return (
    <AppShell activeTab="messages">
      <div className={`dm-wrap${handle ? ' has-thread' : ''}`} data-testid="messages-page">
        <div className="dm-list-pane">
          <div className="dm-list-header">
            <h1 className="dm-title">💬 Messages</h1>
          </div>
          <ThreadList
            threads={threads}
            activeHandle={handle}
            onOpen={(h) => navigate(`/messages/${h}`)}
          />
        </div>
        <div className="dm-thread-pane">
          {handle ? (
            <ThreadView
              handle={handle}
              onBack={() => navigate('/messages')}
              onSent={loadThreads}
            />
          ) : (
            <div className="dm-empty-pane">
              <div className="dm-empty-emoji">👋</div>
              <div className="dm-empty-title">Pick a conversation</div>
              <div className="dm-empty-sub">Or open any creator's profile and hit Message.</div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
